import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import LeftSidebar from './containers/LeftSidebar';
import CenterContent from './containers/CenterContent';
import RightSidebar from './containers/RightSidebar';
import FMOInputViewer from './panels/FMOInputViewer';
import { useZipfilesData } from './hooks/useZipfilesData';
import { useFMOInputsData } from './hooks/useFMOInputsData';
import { usePanelState } from './hooks/usePanelState';

/**
 * InputsTab - Manages FMO input file archives (zipfiles) and their entries
 * 
 * Successfully implements MUI DataGrid Pro tree view to display:
 * - Parent rows: Zipfiles with summary information
 * - Child rows: Spec combinations (clusterspec, pocketspec, inputspec) within each zipfile
 * 
 * @date 2025-08-27 - Fixed tree data display issues
 */
export default function InputsTab() {
  // Selection states
  const [selectedZipfile, setSelectedZipfile] = useState(null);
  const [selectedFMOInput, setSelectedFMOInput] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  // Help dialog state
  const [showBrowserHelp, setShowBrowserHelp] = useState(false);

  // Panel states
  const {
    isLeftPanelCollapsed,
    setIsLeftPanelCollapsed,
    isRightPanelCollapsed,
    setIsRightPanelCollapsed,
    openRightPanel,
    bottomPanelOpen,
    setBottomPanelOpen,
    bottomPanelHeight,
    setBottomPanelHeight,
    openBottomPanel,
  } = usePanelState();
  
  // Data hooks
  const {
    zipfiles,
    loading: zipfilesLoading,
    error: zipfilesError,
    fetchZipfiles,
    totalCount,
    transformToTreeData
  } = useZipfilesData();
  
  const {
    allFMOInputs,
    allFMOInputsLoading,
    error: fmoInputsError,
    fetchAllFMOInputs
  } = useFMOInputsData();
  
  // Fetch all FMO inputs and zipfiles on mount for tree building
  useEffect(() => {
    fetchAllFMOInputs();
    fetchZipfiles(); // Fetch zipfiles once (used for both display and tree)
  }, []);
  
  // Handle tree node selection for filtering
  const handleNodeSelect = useCallback((node) => {
    // Node already has everything pre-calculated in the Browser!
    // It includes: zipfileIds, treeRowIds, and all other metadata
    setSelectedNode(node);
  }, []);
  
  // Handle zipfile or spec row selection
  const handleZipfileSelect = useCallback((row) => {
    setSelectedZipfile(row);
    // The entries panel will filter based on selectedZipfile
  }, []);
  
  // Handle FMO input selection for 3D viewer
  const handleFMOInputSelect = useCallback((fmoInput) => {
    setSelectedFMOInput(fmoInput);
    if (fmoInput) {
      openRightPanel(); // Auto-open viewer
    }
  }, [openRightPanel]);
  
  // Create complete tree data ONCE from all zipfiles and FMO inputs
  const treeData = useMemo(() => {
    // Build tree from ALL zipfiles, not filtered ones
    // Filtering should be done by DataGrid Pro, not by rebuilding tree
    if (!zipfiles || zipfiles.length === 0) return [];
    return transformToTreeData(zipfiles, allFMOInputs || []);
  }, [zipfiles, allFMOInputs, transformToTreeData]);
  
  // Filter tree data based on selectedNode (for actual data filtering like Structures tab)
  const filteredTreeData = useMemo(() => {
    if (!selectedNode || !selectedNode.treeRowIds || selectedNode.treeRowIds.size === 0) {
      return treeData;
    }
    
    // Filter tree data to include only rows that match the selected node
    // This preserves parent-child relationships in the tree
    const filteredRows = [];
    const includedParentIds = new Set();
    
    // First pass: find all matching rows and their parent IDs
    treeData.forEach(row => {
      if (selectedNode.treeRowIds.has(row.id_)) {
        // If this is a spec row, we need to include its parent
        if (row.isSpecRow && row.path && row.path.length > 1) {
          const parentId = row.path[0];
          includedParentIds.add(parentId);
        }
        filteredRows.push(row);
      }
    });
    
    // Second pass: add any missing parent rows
    if (includedParentIds.size > 0) {
      treeData.forEach(row => {
        if (row.isParentRow && includedParentIds.has(row.id_) && 
            !filteredRows.some(r => r.id_ === row.id_)) {
          // Insert parent at the beginning to maintain order
          const firstChildIndex = filteredRows.findIndex(r => 
            r.path && r.path[0] === row.id_
          );
          if (firstChildIndex >= 0) {
            filteredRows.splice(firstChildIndex, 0, row);
          } else {
            filteredRows.unshift(row);
          }
        }
      });
    }
    
    return filteredRows;
  }, [treeData, selectedNode]);
  
  // Count of filtered zipfiles for display
  const filteredZipfileCount = useMemo(() => {
    if (!selectedNode || !selectedNode.zipfileIds || selectedNode.zipfileIds.size === 0) {
      return zipfiles.length;
    }
    
    // Count zipfiles using pre-calculated zipfileIds
    return zipfiles.filter(zipfile => 
      selectedNode.zipfileIds.has(zipfile.id_)
    ).length;
  }, [zipfiles, selectedNode]);
  
  // Filter FMO inputs based on selectedZipfile AND selectedNode (data-level filtering)
  const filteredFMOInputs = useMemo(() => {
    if (!allFMOInputs) return [];
    
    // If a specific zipfile/spec is selected, show only its entries
    if (selectedZipfile && selectedZipfile.entry_ids && selectedZipfile.entry_ids.length > 0) {
      // Convert entry IDs to strings for comparison since id_ field is type 'string'
      const entryIdSet = new Set(selectedZipfile.entry_ids.map(id => String(id)));
      return allFMOInputs.filter(fmoInput => entryIdSet.has(String(fmoInput.id_)));
    }
    
    // If only Browser node is selected, filter by project/target/batch
    if (selectedNode) {
      // Filter entries based on the Browser selection
      if (selectedNode.type === 'project') {
        return allFMOInputs.filter(fmoInput => 
          fmoInput.prep && fmoInput.prep.project === selectedNode.name
        );
      } else if (selectedNode.type === 'target') {
        return allFMOInputs.filter(fmoInput => 
          fmoInput.prep && 
          fmoInput.prep.project === selectedNode.project &&
          fmoInput.prep.target === selectedNode.name
        );
      } else if (selectedNode.type === 'batch') {
        return allFMOInputs.filter(fmoInput => 
          fmoInput.prep && 
          fmoInput.prep.project === selectedNode.project &&
          fmoInput.prep.target === selectedNode.target &&
          (fmoInput.prep.batch || '<none>') === selectedNode.name
        );
      }
    }
    
    // If nothing is selected, return empty array (don't show all entries)
    return [];
  }, [allFMOInputs, selectedZipfile, selectedNode]);
  
  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100%', 
      overflow: 'hidden'
    }}>
      {/* Left Section with Browser */}
      <LeftSidebar
        isLeftPanelCollapsed={isLeftPanelCollapsed}
        setIsLeftPanelCollapsed={setIsLeftPanelCollapsed}
        allFMOInputs={allFMOInputs}
        allZipfiles={zipfiles}  // Use zipfiles instead of allZipfiles
        selectedNode={selectedNode}
        onNodeSelect={handleNodeSelect}
        hasFilter={!!selectedNode}
        filteredZipfileCount={filteredZipfileCount}
        fmoInputsLoading={allFMOInputsLoading}  // Pass loading state for all FMO inputs (tree building)
        showBrowserHelp={showBrowserHelp}
        onBrowserHelpToggle={setShowBrowserHelp}
      />
      
      {/* Center Content Area */}
      <CenterContent
        // Grid props - pass filtered tree data like Structures tab
        zipfiles={zipfiles}  // Still pass all zipfiles for reference
        loading={zipfilesLoading}
        error={zipfilesError}
        selectedZipfile={selectedZipfile}
        onZipfileSelect={handleZipfileSelect}
        onRefresh={() => fetchZipfiles()}
        totalCount={zipfiles.length}  // Total count of ALL zipfiles
        treeData={filteredTreeData}  // Pass FILTERED tree data for display
        
        // FMO Inputs panel props
        bottomPanelOpen={bottomPanelOpen}
        setBottomPanelOpen={setBottomPanelOpen}
        bottomPanelHeight={bottomPanelHeight}
        setBottomPanelHeight={setBottomPanelHeight}
        fmoInputs={filteredFMOInputs}  // Pass FILTERED FMO inputs like the grid above
        fmoInputsLoading={allFMOInputsLoading}  // Use allFMOInputsLoading
        fmoInputsError={fmoInputsError}
        selectedFMOInput={selectedFMOInput}
        onFMOInputSelect={handleFMOInputSelect}
        selectedNode={selectedNode}  // Pass selectedNode for title context
      />
      
      {/* Right Panel - FMO Input Viewer */}
      <FMOInputViewer
        fmoInput={selectedFMOInput}
        isCollapsed={isRightPanelCollapsed}
        onCollapse={() => setIsRightPanelCollapsed(true)}
      />
      
      {/* Right Sidebar */}
      <RightSidebar
        isRightPanelCollapsed={isRightPanelCollapsed}
        onTogglePanel={setIsRightPanelCollapsed}
      />
    </Box>
  );
}